import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useOutletContext } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import CategoriesSlider from '../../components/categories-slider/CategoriesSlider'
import CategoryCountdown from '../../components/categories-slider/CategoryCountdown'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faHeart, faLeaf, faStar } from '@fortawesome/free-solid-svg-icons'
import { categoryIcon } from '../../components/categories-slider/categoryIcon'
import MenuItem from '../../components/menu-item/MenuItem'
import Loader from '../../components/loader/Loader'
import { useGetCategoriesQuery } from '../../store/api/categoriesApi'
import { useGetProductsQuery } from '../../store/api/productsApi'
import { useLayout } from '../../context/LayoutContext'
import DaysOffModal from './DaysOffModal/DaysOffModal'
import AnnouncementModal from './AnnouncementModal/AnnouncementModal'
import { IProduct } from '../../types/products'
import { IProductGroup } from '../../types/app'
import { getGeneralInfo } from '../../utils/generalInfo'
import { imageUrl } from '../../utils/imageUrl'
import { PandaSign } from '../../components/panda-sign/PandaSign'
import { iconBurst } from '../../utils/fx'
import { useDeviceDetect } from '../../utils/use-device-detect'
import './Home.scss'

const c = 'home-page'
const PANDA_QUERY = /panda|панд/i

const Home = () => {
  const [openModal, setOpenModal] = useState(false)
  const [openAnnouncement, setOpenAnnouncement] = useState(false)
  const hasRestoredScroll = useRef(false)
  const { cart, setCart, searchValue } = useOutletContext<{
    cart: IProduct[]
    setCart: React.Dispatch<React.SetStateAction<IProduct[]>>
    searchValue: string
  }>()
  const { setSearchValue, setSearchOpen } = useLayout()
  const { isMobile, isTablet } = useDeviceDetect()
  const generalInfo = getGeneralInfo()
  const { t, i18n } = useTranslation()

  const { data: categories = [] } = useGetCategoriesQuery()
  const { data: products = [], isLoading: loadingProducts } =
    useGetProductsQuery(undefined)

  const getTranslatedName = (item: IProductGroup) => {
    if (i18n.language === 'ru' && item?.category_name_ru) {
      return item?.category_name_ru
    }

    return item?.category_name
  }

  useEffect(() => {
    if (loadingProducts || !products.length) return
    const urls: string[] = []
    for (const group of products as IProductGroup[]) {
      for (const item of group.items || []) {
        if (item?.image) urls.push(imageUrl(item.image, 480))
      }
    }
    if (!urls.length) return
    const ric =
      (window as unknown as { requestIdleCallback?: (cb: () => void) => number })
        .requestIdleCallback || ((cb: () => void) => window.setTimeout(cb, 300))
    let i = 0
    const warm = () => {
      const batch = urls.slice(i, i + 8)
      if (!batch.length) return
      batch.forEach((u) => {
        const img = new Image()
        img.decoding = 'async'
        img.src = u
      })
      i += batch.length
      if (i < urls.length) ric(warm)
    }
    ric(warm)
  }, [loadingProducts, products])

  useEffect(() => {
    if (!loadingProducts && !hasRestoredScroll.current) {
      hasRestoredScroll.current = true
      window.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [loadingProducts])

  useEffect(() => {
    if (generalInfo?.dayOff) {
      setOpenModal(true)
    }

    if (generalInfo?.announcement) {
      setOpenAnnouncement(true)
    }
  }, [])

  const filteredProducts = useMemo(() => {
    if (searchValue === '') {
      return products
    }

    return products.map((product: IProductGroup) => {
      const updatedItems = product.items?.filter(
        (item: IProduct) =>
          (item.name &&
            item.name?.toLowerCase().includes(searchValue.toLowerCase())) ||
          (item.ru_name &&
            item.ru_name?.toLowerCase().includes(searchValue.toLowerCase())),
      )

      return {
        ...product,
        items: updatedItems,
      }
    })
  }, [searchValue, products])

  const available = useMemo(
    () =>
      new Set(
        (filteredProducts as IProductGroup[])
          .filter((group) => group.items?.length)
          .map((group) => group.category_id),
      ),
    [filteredProducts],
  )

  const hasResults = available.size > 0
  const foundPanda = !hasResults && PANDA_QUERY.test(searchValue)

  useEffect(() => {
    if (foundPanda) {
      iconBurst(window.innerWidth / 2, window.innerHeight / 2, [faHeart, faLeaf, faStar], 14)
    }
  }, [foundPanda])

  function clearSearch() {
    setSearchValue('')
    setSearchOpen(false)
  }

  return (
    <div className={c}>
      <CategoriesSlider
        data={categories}
        available={available}
        showSearch={isMobile || isTablet}
      />
      <div className={`${c}__menu`}>
        {loadingProducts ? (
          <Loader />
        ) : hasResults ? (
          filteredProducts.map((product: IProductGroup) => {
            if (!product.items.length) {
              return null
            }

            const cat = categories.find((c) => c._id === product.category_id)
            return (
              <section
                key={product.category_id}
                id={`category-${product.category_id}`}
                className={`${c}__section`}
              >
                <h2 className={`${c}__title`}>
                  <span className={`${c}__title-icon`}>
                    <FontAwesomeIcon icon={categoryIcon(product.category_name)} />
                  </span>
                  <span>{getTranslatedName(product)}</span>
                  {cat?.isNew && (
                    <span className={`${c}__new-badge`}>{t('NEW')}</span>
                  )}
                  {cat?.isNew && cat?.specialOfferEnd && (
                    <CategoryCountdown endDate={cat.specialOfferEnd} />
                  )}
                  <span className={`${c}__count`}>{product.items.length}</span>
                </h2>
                <div className={`${c}__grid`}>
                  {product.items.map((item: IProduct, i: number) => (
                    <MenuItem
                      key={item._id ?? i}
                      index={i}
                      data={item}
                      cart={cart}
                      setCart={setCart}
                    />
                  ))}
                </div>
              </section>
            )
          })
        ) : (
          <div className={`${c}__empty`}>
            <PandaSign
              size={104}
              signKey={String(foundPanda)}
              className={`${c}__empty-panda${foundPanda ? ' is-found' : ''}`}
            >
              {foundPanda ? t('SIGN_HI') : '?'}
            </PandaSign>
            {searchValue ? (
              <>
                <h3>{foundPanda ? t('FOUND_PANDA') : t('NO_RESULTS_TITLE')}</h3>
                <p>
                  {foundPanda
                    ? t('PANDA_NOT_ON_MENU')
                    : t('NO_RESULTS_TEXT', { query: searchValue })}
                </p>
                <button onClick={clearSearch}>{t('CLEAR_SEARCH')}</button>
              </>
            ) : (
              <h3>{t('NO_PRODUCTS')}</h3>
            )}
          </div>
        )}
      </div>
      {openModal && (
        <DaysOffModal isOpen={openModal} setIsOpen={setOpenModal} />
      )}
      {openAnnouncement && (
        <AnnouncementModal
          isOpen={openAnnouncement}
          setIsOpen={setOpenAnnouncement}
        />
      )}
    </div>
  )
}

export default Home
